import { useCallback, useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export const NotificationBell = ({ onOpened }) => {
  const [data, setData] = useState({ unread: 0, items: [] });

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get("/company/notifications");
      setData(res);
    } catch (e) {
      /* the bell is non-critical */
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [load]);

  const opened = async (isOpen) => {
    if (!isOpen || !data.unread) return;
    await api.post("/company/notifications/read").catch(() => {});
    await load();
    onOpened?.();
  };

  return (
    <DropdownMenu onOpenChange={opened}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="notification-bell"
          className="relative h-11 w-11 rounded-full border-slate-300 p-0 transition-colors duration-200 hover:bg-slate-100"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4 text-slate-700" />
          {data.unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1 text-[11px] font-bold text-white"
            >
              {data.unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notification-list">
        <div className="border-b border-slate-100 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
          Notifications
        </div>
        {data.items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-500" data-testid="notification-empty">
            Nothing yet. Approvals from your customers show up here.
          </p>
        ) : (
          <div className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
            {data.items.map((n) => (
              <div
                key={n.id}
                data-testid={`notification-${n.id}`}
                className={`px-4 py-3 text-sm ${n.read ? "text-slate-600" : "bg-blue-50/60 font-medium text-slate-900"}`}
              >
                {n.message}
                <div className="mt-1 text-xs text-slate-400">
                  {(n.created_at || "").slice(0, 10)}
                </div>
              </div>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
